import React, { useState, useEffect, useRef } from "react"
import { withRouter } from "react-router-dom"
import { useSelector } from "react-redux"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import OfferForm from "./offer-form"
import OfferTagsForm from "./offer-tags-form"
import OfferInclusionsForm from "./offer-inclusions-form"
import OfferExclusionsForm from "./offer-exclusions-form"
import OfferTermsForm from "./offer-terms-form"
import {
  Alert,
  Col,
  Container,
  Row,
  TabContent,
  TabPane,
} from "reactstrap"
import { PageTitle } from "constants/ui"
import { getTabNames, getTabPermissions, isTabMandatory } from "helpers/tab_helper"
import PermissionTabs from "components/Common/PermissionTabs"
import CButton from "components/Common/CButton"
import { getUserAuthorized } from "helpers/backend_helper"

// State banners shown on the offer detail screen (2026-08-20). The pipeline now
// stops short at POSTED, so the operator needs the workflow state visible while
// validating. Keys are lowercase offer states from the workflow queues.
const STATE_BANNERS = {
  initiated: { color: "secondary", text: "Not yet posted. Complete the offer setup and post it to move it to Operations." },
  posted: { color: "warning", text: "Awaiting validation. Verify municipality tax applicability per outlet, reconciliation frequency and fees against the agreement, and offer wording — then Process this offer from the Operations queue." },
  processed: { color: "info", text: "Processed — awaiting publish approval in the Approver queue." },
  published: { color: "success", text: "Published — this offer is live." },
  rejected: { color: "danger", text: "Rejected — see the offer state history for details." },
  denied: { color: "danger", text: "Denied — see the offer state history for details." },
}

const OfferDetail = props => {
  const { match, location } = props

  const page_type = match?.params?.id && match?.params?.id !== "add" ? "detail" : "add"
  
  // Parse query parameters for copy functionality
  const urlParams = new URLSearchParams(location.search)
  const copyFromID = urlParams.get('copyFrom')
  const fromType = urlParams.get('fromType')

  const [activeTab, setActiveTab] = useState("1")
  // Only add tab flow state for create mode
  const [completedTabs, setCompletedTabs] = useState(
    page_type === "detail" ? ["1", "2", "3", "4", "5"] : []
  )
  const [enabledTabs, setEnabledTabs] = useState(
    page_type === "detail" ? ["1", "2", "3", "4", "5"] : ["1"]
  ) // Track which tabs are enabled
  const [offerCreated, setOfferCreated] = useState(
    page_type === "detail"
  )

 
  const [createdOfferID, setCreatedOfferID] = useState(null)
  const hasInitializedPermissions = useRef(false)

  // Permission checking state
  const [tabPermissions, setTabPermissions] = useState({})
  const [isCheckingPermission, setIsCheckingPermission] = useState(false)
  const [permissionErrors, setPermissionErrors] = useState({})
  const [isInitialized, setIsInitialized] = useState(false)

  // State to track offer info for breadcrumb
  const [offerInfo, setOfferInfo] = useState({
    merchantLegalName: "",
    merchantLegalEntityAddress: "",
    cashbackAndLabel: "",
    state: ""
  })

  // Determine offer type from pathname
  const getOfferType = () => {
    const pathname = location.pathname
    if (pathname.includes('/curator-offers')) return 'curator'
    if (pathname.includes('/operation-offers')) return 'operation'
    if (pathname.includes('/approver-offers')) return 'approver'
    if (pathname.includes('/published-offers')) return 'published'
    return 'curator' // default
  }

  const offerType = getOfferType()

  // Determine if this offer type can be edited
  const canEdit = offerType === 'curator' || offerType === 'operation' || offerType === 'published'

  // Set page titles based on offer type
  const getPageTitles = () => {
    switch (offerType) {
      case 'curator':
        return {
          list: PageTitle.CURATOR_OFFERS_LIST,
          detail: PageTitle.CURATOR_OFFERS_DETAIL,
          add: PageTitle.CURATOR_OFFERS_ADD,
          listPath: '/curator-offers',
          pageKey: 'CURATOR_OFFERS_DETAIL'
        }
      case 'operation':
        return {
          list: PageTitle.OPERATION_OFFERS_LIST,
          detail: PageTitle.OPERATION_OFFERS_DETAIL,
          add: PageTitle.OPERATION_OFFERS_ADD,
          listPath: '/operation-offers',
          pageKey: 'OPERATION_OFFERS_DETAIL'
        }
      case 'approver':
        return {
          list: PageTitle.APPROVER_OFFERS_LIST,
          detail: PageTitle.APPROVER_OFFERS_DETAIL,
          add: PageTitle.APPROVER_OFFERS_ADD || 'Add Approver Offer',
          listPath: '/approver-offers',
          pageKey: 'APPROVER_OFFERS_DETAIL'
        }
      case 'published':
        return {
          list: PageTitle.PUBLISHED_OFFERS_LIST,
          detail: PageTitle.PUBLISHED_OFFERS_DETAIL,
          add: PageTitle.PUBLISHED_OFFERS_ADD || 'Add Published Offer',
          listPath: '/published-offers',
          pageKey: 'PUBLISHED_OFFERS_DETAIL'
        }
      default:
        return {
          list: 'Offers',
          detail: 'Offer Detail',
          add: 'Add Offer',
          listPath: '/curator-offers',
          pageKey: 'CURATOR_OFFERS_DETAIL'
        }
    }
  }

  const pageTitles = getPageTitles()
  document.title = page_type === "add" ? pageTitles.add : pageTitles.detail

  const { userMenu, offers } = useSelector(state => ({
    userMenu: state.userMenu?.userMenu || [],
    offers: state.offers || {}
  }))

  const pageKey = pageTitles.pageKey
  const tabNames = getTabNames(pageKey, userMenu)
  const tabPermissionsArray = getTabPermissions(pageKey, page_type, userMenu)

  // Generic form state checker
  const hasFormContent = (tabName) => {
    switch (tabName.toLowerCase()) {
      case 'offer':
        return offerCreated
      case 'tags':
        return offers.offerTags && offers.offerTags.length > 0
      case 'inclusions':
        return offers.offerInclusions && offers.offerInclusions.length > 0
      case 'exclusions':
        return offers.offerExclusions && offers.offerExclusions.length > 0
      case 'terms & conditions':
        return offers.offerTermsConditions && offers.offerTermsConditions.length > 0
      default:
        return false
    }
  }

  // Permission checking function with API failure handling
  const checkTabPermission = async (tabId, forceRefresh = false) => {
    // Skip permission check for detail mode
    if (page_type === "detail") {
      return true
    }

    // Use cached permission if available and not forcing refresh
    if (!forceRefresh && tabPermissions[tabId] !== undefined) {
      return tabPermissions[tabId]
    }

    const permission = tabPermissionsArray[tabId - 1]
    if (!permission || !permission.contextUri || !permission.actionType) {
      console.warn(`No valid permission config for tab ${tabId}`)
      return true
    }

    try {
      setIsCheckingPermission(true)
      setPermissionErrors(prev => ({ ...prev, [tabId]: null }))

      const result = await getUserAuthorized({
        headers: {
          "input-parameters": {
            requestURI: permission.contextUri,
            requestMethod: permission.actionType,
          },
        },
      })

      // Check if permission is granted based on API response
      const hasPermission =
        result?.type === "USER_AUTHORIZATION_STATUS" &&
        result?.desc === "true" &&
        result?.code === "200"

      setTabPermissions(prev => ({ ...prev, [tabId]: hasPermission }))
      return hasPermission
    } catch (error) {
      console.error(`Permission check failed for tab ${tabId}:`, error)

      // Handle API failure - default to deny for security but mark as error
      setPermissionErrors(prev => ({
        ...prev,
        [tabId]: {
          message: "Unable to verify permissions. Please try again.",
          isApiError: true,
        },
      }))
      setTabPermissions(prev => ({ ...prev, [tabId]: false }))
      return false
    } finally {
      setIsCheckingPermission(false)
    }
  }

  // Reset state when in add mode (handles both copy and fresh add)
  // Use location.key to detect actual navigation changes, not just param changes
  useEffect(() => {
    if (page_type === "add") {
      setActiveTab("1")
      setCompletedTabs([])
      setEnabledTabs(["1"])
      setOfferCreated(false)
      setCreatedOfferID(null)
      setTabPermissions({})
      setPermissionErrors({})
      hasInitializedPermissions.current = false
      setIsInitialized(false)
      setOfferInfo({
        merchantLegalName: "",
        merchantLegalEntityAddress: "",
        cashbackAndLabel: "",
        state: ""
      })
    }
  }, [page_type, copyFromID, location.pathname, location.key])

  // Check permission for offer tab on page load
  useEffect(() => {
    const initializePermissions = async () => {
      if (page_type === "add" &&
        tabPermissionsArray.length > 0 &&
        !isInitialized &&
        !hasInitializedPermissions.current) {

        hasInitializedPermissions.current = true


        if (tabPermissionsArray[0] && tabPermissionsArray[0].contextUri) {
          await checkTabPermission(1)
          setIsInitialized(true)
        }
      }
    }

    initializePermissions()
  }, [page_type, tabPermissionsArray.length, isInitialized])

  // Update offer info from Redux in detail mode
  useEffect(() => {
    if (page_type === "detail" && match?.params?.id) {
      const offerID = match.params.id

      // Get offer from appropriate state based on offerType
      let offer = null
      if (offerType === 'curator' && offers.curatorOffers) {
        offer = offers.curatorOffers.find(o => o.offerID === offerID)
      } else if (offerType === 'operation' && offers.operationOffers) {
        offer = offers.operationOffers.find(o => o.offerID === offerID)
      } else if (offerType === 'approver' && offers.approverOffers) {
        offer = offers.approverOffers.find(o => o.offerID === offerID)
      } else if (offerType === 'published' && offers.publishedOffers) {
        offer = offers.publishedOffers.find(o => o.offerID === offerID)
      }

      if (offer) {
        const cashbackText = offer.userCashbackPercentage && offer.label
          ? `${offer.userCashbackPercentage}% on ${offer.label}`
          : ""

        setOfferInfo({
          merchantLegalName: offer.merchant?.legalEntityName || "",
          merchantLegalEntityAddress: offer.merchant?.legalEntityAddress || "",
          cashbackAndLabel: cashbackText,
          state: offer.state || ""
        })
      }
    }
  }, [page_type, match?.params?.id, offerType, offers])

  const toggle = tab => {
    // For detail mode, keep original behavior
    if (page_type === "detail") {
      if (activeTab !== tab) {
        setActiveTab(tab)
      }
      return
    }

    // For create mode, only allow navigation to enabled tabs
    if (enabledTabs.includes(tab)) {
      setActiveTab(tab)
    }
  }

  const handleOfferCreation = offerID => {
    setOfferCreated(true)
    setCreatedOfferID(offerID)
    setCompletedTabs(prev => (prev.includes("1") ? prev : [...prev, "1"]))
    // Enable the next tab after offer creation
    setEnabledTabs(prev => {
      if (!prev.includes("2")) {
        return [...prev, "2"]
      }
      return prev
    })
  }

  const handleTabCompletion = tabId => {
    if (!completedTabs.includes(tabId)) {
      setCompletedTabs(prev => [...prev, tabId])
    }
  }

  // Handler for form changes (called from offer-form)
  const handleFormChange = (formValues) => {
    if (formValues) {
      const cashbackText = formValues.userCashbackPercentage && formValues.offerLabel
        ? `${formValues.userCashbackPercentage}% on ${formValues.offerLabel}`
        : ""

      // Preserve `state` from the Redux-list effect: form values don't carry the
      // workflow state, so without prev this would wipe the banner.
      setOfferInfo(prev => ({
        merchantLegalName: formValues.merchantLegalName || "",
        merchantLegalEntityAddress: formValues.merchantLegalEntityAddress || "",
        cashbackAndLabel: cashbackText,
        state: prev.state
      }))
    }
  }

  const navigateToNextTab = async () => {
    const currentTabIndex = parseInt(activeTab)
    const nextTab = String(currentTabIndex + 1)
    if (currentTabIndex < tabNames.length) {
      // Enable the next tab when navigating
      setEnabledTabs(prev => {
        if (!prev.includes(nextTab)) {
          return [...prev, nextTab]
        }
        return prev
      })

      // Check permission for next tab on forward navigation (force refresh)
      if (page_type === "add" && tabPermissionsArray[parseInt(nextTab) - 1]) {
        const permission = tabPermissionsArray[parseInt(nextTab) - 1]
        if (permission && permission.contextUri) {
          await checkTabPermission(parseInt(nextTab), true)
        }
      }

      setActiveTab(nextTab)
    }
  }

  const navigateToPreviousTab = () => {
    const currentTabIndex = parseInt(activeTab)
    const previousTab = String(currentTabIndex - 1)
    if (currentTabIndex > 1) {
      setActiveTab(previousTab)
    }
  }

  const isTabDisabled = tabIndex => {
    if (page_type === "detail") return false // All tabs enabled for detail/edit mode
    return !enabledTabs.includes(String(tabIndex)) // Tab is disabled if not in enabledTabs
  }

  console.log(activeTab, completedTabs, enabledTabs, tabPermissions, permissionErrors, "isCheckingPermission:", isCheckingPermission)

  // No Access Message Component
  const NoAccessMessage = ({ tabId, onRetry }) => {
    const permissionError = permissionErrors[tabId]
    const isApiError = permissionError?.isApiError

    return (
      <div className="text-center p-5">
        <i
          className={`display-4 mb-3 ${isApiError
            ? "mdi mdi-wifi-off text-warning"
            : "mdi mdi-lock-outline text-muted"
            }`}
        ></i>
        <h5>{isApiError ? "Connection Error" : "Access Restricted"}</h5>
        <p className="text-muted">
          {isApiError
            ? "Unable to verify permissions. Please try again."
            : "You do not have access to this module. Contact your administrator for access rights."}
        </p>
        {isApiError && (
          <CButton.Primary
            text="Retry"
            onClick={() => checkTabPermission(tabId, true)}
            className="mt-3"
          />
        )}
      </div>
    )
  }

  const renderTabContent = (tabName, tabIndex) => {
    // Generate a unique key for OfferForm that changes when navigating between add modes
    // This ensures the form component is completely reset when navigating from copy to add or vice versa
    const formKey = page_type === "add"
      ? `offer-form-${copyFromID || 'new'}-${location.key || 'initial'}`
      : `offer-form-detail-${match?.params?.id || 'unknown'}`

    switch (tabName.toLowerCase()) {
      case 'offer':
        return (
          <OfferForm
            key={formKey}
            page={page_type}
            match={match}
            history={props.history}
            offerType={offerType}
            canEdit={canEdit}
            onOfferCreated={handleOfferCreation}
            onTabCompletion={handleTabCompletion}
            navigateToNextTab={navigateToNextTab}
            activeTab={activeTab}
            createdOfferID={createdOfferID}
            onFormChange={handleFormChange}
          />
        )

      case 'tags':
        return (
          <OfferTagsForm
            page={page_type}
            match={match}
            history={props.history}
            offerType={offerType}
            canEdit={canEdit}
            onTabCompletion={handleTabCompletion}
            activeTab={activeTab}
            tabId={String(tabIndex)}
            createdOfferID={createdOfferID}
            copyFromID={copyFromID}
            fromType={fromType}
          />
        )

      case 'inclusions':
        return (
          <OfferInclusionsForm
            page={page_type}
            match={match}
            history={props.history}
            offerType={offerType}
            canEdit={offerType === 'published' ? false : canEdit}
            onTabCompletion={handleTabCompletion}
            activeTab={activeTab}
            tabId={String(tabIndex)}
            createdOfferID={createdOfferID}
            copyFromID={copyFromID}
            fromType={fromType}
          />
        )

      case 'exclusions':
        return (
          <OfferExclusionsForm
            page={page_type}
            match={match}
            history={props.history}
            offerType={offerType}
            canEdit={offerType === 'published' ? false : canEdit}
            onTabCompletion={handleTabCompletion}
            activeTab={activeTab}
            tabId={String(tabIndex)}
            createdOfferID={createdOfferID}
            copyFromID={copyFromID}
            fromType={fromType}
          />
        )

      case 'terms & conditions':
        return (
          <OfferTermsForm
            page={page_type}
            match={match}
            history={props.history}
            offerType={offerType}
            canEdit={offerType === 'published' ? false : canEdit}
            onTabCompletion={handleTabCompletion}
            activeTab={activeTab}
            tabId={String(tabIndex)}
            createdOfferID={createdOfferID}
            copyFromID={copyFromID}
            fromType={fromType}
          />
        )

      default:
        return (
          <div className="text-center p-5">
            <h5>Tab content not implemented</h5>
            <p className="text-muted">Content for "{tabName}" tab is not yet implemented.</p>
          </div>
        )
    }
  }

  const handleFinish = () => {
    if (createdOfferID) {
      props.history.push(`${pageTitles.listPath}/${createdOfferID}`)
    } else {
      props.history.push(pageTitles.listPath)
    }
  }

  const handleSkipFinish = () => {
    props.history.push(pageTitles.listPath)
  }

  const renderNavigationButtons = (tabName, tabIndex) => {
    const isLastTab = tabIndex >= tabNames.length
    const isMandatory = isTabMandatory(pageKey, tabName, userMenu)
    const hasContent = hasFormContent(tabName)

    if (isLastTab) {
      if (isMandatory) {
        return hasContent ?
          <CButton.Primary text="Finish" onClick={handleFinish} disabled={isCheckingPermission} /> :
          false
      } else {
        return hasContent ?
          <CButton.Primary text="Finish" onClick={handleFinish} disabled={isCheckingPermission} /> :
          <CButton.Secondary text="Skip & Finish" onClick={handleSkipFinish} disabled={isCheckingPermission} />
      }
    }

    if (isMandatory) {
      return hasContent ?
        <CButton.Primary text="Continue" onClick={navigateToNextTab} disabled={isCheckingPermission || isLastTab} /> : false

    } else {
      return hasContent ?
        <CButton.Primary text="Continue" onClick={navigateToNextTab} disabled={isCheckingPermission || isLastTab} /> :
        <CButton.Secondary text="Skip" onClick={navigateToNextTab} disabled={isCheckingPermission || isLastTab} />
    }
  }

  // Loading state while checking initial permissions
  if (page_type === "add" && !isInitialized && isCheckingPermission) {
    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid={true}>
            <Breadcrumbs
              titleLink={pageTitles.listPath}
              title={pageTitles.list}
              breadcrumbItem={pageTitles.add}
            />
          </Container>
        </div>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            titleLink={pageTitles.listPath}
            title={pageTitles.list}
            breadcrumbItem={page_type === "add" ? pageTitles.add : pageTitles.detail}
            secondaryInfo={[
              { label: "Merchant Legal Name", value: offerInfo.merchantLegalName },
              { label: "Merchant Legal Entity Address", value: offerInfo.merchantLegalEntityAddress },
              { label: "Cashback and Label", value: offerInfo.cashbackAndLabel }
            ]}
          />
          {page_type === "detail" && STATE_BANNERS[(offerInfo.state || "").toLowerCase()] && (
            <Alert color={STATE_BANNERS[(offerInfo.state || "").toLowerCase()].color} className="d-flex align-items-center">
              <i className="mdi mdi-information-outline me-2 font-size-18"></i>
              <div>
                <strong>Offer state: {(offerInfo.state || "").toUpperCase()}</strong>
                <div>{STATE_BANNERS[(offerInfo.state || "").toLowerCase()].text}</div>
              </div>
            </Alert>
          )}
          <Row>
            <Col md="12">
              <PermissionTabs
                tabs={tabNames}
                activeTab={activeTab}
                onTabChange={toggle}
                isTabDisabled={isTabDisabled}
                tabPermissions={tabPermissionsArray}
                history={props.history}
              />

              <TabContent activeTab={activeTab} className="p-3 text-muted">
                {tabNames.map((tabName, index) => {
                  const tabIndex = index + 1
                  const tabId = String(tabIndex)

                  return (
                    <TabPane key={tabId} tabId={tabId}>
                      {tabPermissions[tabIndex] === false ? (
                        <NoAccessMessage tabId={tabIndex} />
                      ) : (
                        renderTabContent(tabName, tabIndex)
                      )}

                      {/* Navigation buttons for add mode */}
                      {page_type === "add" && tabPermissions[tabIndex] !== false && (
                        <div className="mt-3 d-flex justify-content-between">
                          <CButton.Secondary
                            text={tabIndex === 1 ? "Cancel" : "Back"}
                            onClick={() =>
                              tabIndex === 1
                                ? props.history.push(pageTitles.listPath)
                                : navigateToPreviousTab()
                            }
                            disabled={tabIndex === 1 ? false : parseInt(activeTab) <= 1}
                          />
                          <div>
                            {renderNavigationButtons(tabName, tabIndex)}
                          </div>
                        </div>
                      )}
                    </TabPane>
                  )
                })}
              </TabContent>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(OfferDetail)